/**
 * Determination → the shapes the design asks for.
 *
 * The design was drawn against a fixture: 21 requirements, 43% ready, six
 * categories, a named applicant and sponsor. This maps our real determination
 * onto those shapes, and the interesting part is the places where it cannot.
 *
 * ONE STATUS THE DESIGN DOES NOT HAVE. It uses three: settled, in progress, not
 * applicable — plus a "Blocked" filter. We have a fourth,
 * ASSESSED_BY_CASEWORKER, and there is no honest way to fold it into any of
 * them. "Settled" would tell someone a caseworker's judgement has gone their
 * way, which is the exact false tick ADR-0022 exists to prevent; "in progress"
 * implies work they can do; "not applicable" is false. So it gets its own
 * state, `judged`, with its own neutral treatment — an addition to the design,
 * made deliberately rather than by squeezing.
 *
 * READINESS EXCLUDES THE JUDGED ONES from the denominator as well as the
 * numerator. Counting them as outstanding would peg the number below 100%
 * forever, since nothing the user does can settle them; counting them as
 * settled would overstate. Leaving them out means the percentage answers a
 * question the user can act on — "how much of what I control is done" — and
 * they are surfaced separately rather than hidden.
 */
import type { Determination, RequirementResult } from "@verity/engine";
import type { Ruleset } from "@verity/rules";

export type ItemState = "settled" | "progress" | "judged" | "not-applicable";

export interface RequirementView {
  ruleId: string;
  title: string;
  state: ItemState;
  statusLabel: string;
  /** The rule in the adviser's own words — the design's "The rule, plainly". */
  plainly: string;
  citation: { label: string; url: string; published: string };
  evidence: { label: string; met: boolean; notes: string[] }[];
  working: { expression: string; result: string; note?: string }[];
  eligibleFrom?: string;
  categoryKey: string;
  categoryName: string;
}

export interface CategoryView {
  key: string;
  name: string;
  settled: number;
  total: number;
  /** 0–1. Judged requirements are excluded, as in `readiness`. */
  fraction: number;
  assessment: string;
}

export interface NextItem {
  ruleId: string;
  title: string;
  note: string;
  categoryName: string;
}

export interface Workspace {
  readiness: number;
  readinessNote: string;
  settled: number;
  outstanding: number;
  judged: number;
  requirements: RequirementView[];
  categories: CategoryView[];
  next: NextItem[];
}

/**
 * The six categories the design shows, mapped onto our rule-id prefixes.
 * Fees and the health surcharge are in the design and NOT here: the engine
 * encodes the Immigration Rules, and a fee is not one of them.
 */
const CATEGORIES: { key: string; name: string; prefix: string }[] = [
  { key: "financial", name: "Financial requirement", prefix: "FIN." },
  { key: "relationship", name: "Relationship", prefix: "REL." },
  { key: "accommodation", name: "Accommodation", prefix: "ACC." },
  { key: "english", name: "English language", prefix: "ENG." },
  { key: "sponsor", name: "Your partner's status", prefix: "SPN." },
  { key: "koll", name: "Life in the UK", prefix: "KOLL." },
  { key: "documents", name: "Documents and formats", prefix: "EVD." },
];

const STATE_LABEL: Record<ItemState, string> = {
  settled: "Settled",
  progress: "In progress",
  judged: "A caseworker decides this",
  "not-applicable": "Does not apply",
};

function stateOf(status: RequirementResult["status"]): ItemState {
  switch (status) {
    case "MET":
      return "settled";
    case "NOT_APPLICABLE":
      return "not-applicable";
    case "ASSESSED_BY_CASEWORKER":
      return "judged";
    default:
      return "progress";
  }
}

function categoryFor(ruleId: string): { key: string; name: string } {
  const found = CATEGORIES.find((c) => ruleId.startsWith(c.prefix));
  return found ? { key: found.key, name: found.name } : { key: "other", name: "Other" };
}

/**
 * What to do next, "ordered by what holds up the rest".
 *
 * A requirement that is NOT_MET is ordered ahead of one merely waiting on
 * information, because it needs a decision or a change rather than a document.
 * Within each, the engine's own order is kept — it runs gates before details,
 * so a route-ending question already comes first.
 */
function nextFor(requirements: RequirementView[]): NextItem[] {
  const blocked = requirements.filter((r) => r.state === "progress" && r.evidence.length === 0);
  const waiting = requirements.filter((r) => r.state === "progress" && r.evidence.length > 0);

  return [...blocked, ...waiting].slice(0, 3).map((r) => ({
    ruleId: r.ruleId,
    title: r.title,
    note:
      r.evidence.find((e) => !e.met)?.label ??
      (r.eligibleFrom ? `On your answers this is first met on ${r.eligibleFrom}.` : r.plainly),
    categoryName: r.categoryName,
  }));
}

function assessmentFor(settled: number, total: number, judged: number): string {
  if (total === 0 && judged > 0) return "Decided by a caseworker on your evidence.";
  if (total === 0) return "Nothing to do here for this application.";
  if (settled === total) return "Everything here is settled on your answers.";
  if (settled === 0) return "Not started.";
  return `${total - settled} still open.`;
}

export function buildWorkspace(determination: Determination, ruleset: Ruleset): Workspace {
  const titles = new Map(ruleset.rules.map((r) => [r.id, r.title]));

  const requirements: RequirementView[] = determination.requirements.map((r) => {
    const category = categoryFor(r.rule_id);
    const state = stateOf(r.status);
    return {
      ruleId: r.rule_id,
      title: titles.get(r.rule_id) ?? r.rule_id,
      state,
      statusLabel: STATE_LABEL[state],
      plainly: r.plain_english,
      citation: {
        label: `${r.citation.instrument}, ${r.citation.paragraph}`,
        url: r.citation.url,
        published: r.citation.published,
      },
      evidence: r.evidence_required.map((e) => ({
        label: e.label,
        // We have never seen a document. "Met" here means the requirement it
        // supports is met on the answers given — the design's tick, with our
        // meaning, and the copy alongside it says so.
        met: r.status === "MET",
        notes: [...(e.format_rules ?? [])],
      })),
      working: [],
      ...(r.eligible_from ? { eligibleFrom: r.eligible_from } : {}),
      categoryKey: category.key,
      categoryName: category.name,
    };
  });

  const live = requirements.filter((r) => r.state !== "not-applicable");
  const countable = live.filter((r) => r.state !== "judged");
  const settled = countable.filter((r) => r.state === "settled").length;
  const outstanding = countable.length - settled;
  const judged = live.filter((r) => r.state === "judged").length;
  const readiness = countable.length === 0 ? 0 : Math.round((settled / countable.length) * 100);

  const categories: CategoryView[] = CATEGORIES.map((c) => {
    const inCategory = live.filter((r) => r.categoryKey === c.key);
    const countableHere = inCategory.filter((r) => r.state !== "judged");
    const settledHere = countableHere.filter((r) => r.state === "settled").length;
    const judgedHere = inCategory.length - countableHere.length;
    return {
      key: c.key,
      name: c.name,
      settled: settledHere,
      total: countableHere.length,
      fraction: countableHere.length === 0 ? 0 : settledHere / countableHere.length,
      assessment: assessmentFor(settledHere, countableHere.length, judgedHere),
    };
  }).filter((c) => c.total > 0 || c.assessment.startsWith("Decided"));

  return {
    readiness,
    readinessNote: readinessNote(readiness, outstanding, judged),
    settled,
    outstanding,
    judged,
    requirements,
    categories,
    next: nextFor(requirements),
  };
}

/**
 * The design is emphatic that the number alone is not useful and the sentence
 * beside it is. It must also stay inside §3.1.3 — no likelihood, no prediction,
 * no "on track". It describes what is done and what is left, and nothing else.
 */
function readinessNote(readiness: number, outstanding: number, judged: number): string {
  const judgedClause =
    judged > 0
      ? ` ${judged} more ${judged === 1 ? "requirement is" : "requirements are"} decided by a caseworker on your evidence, so ${judged === 1 ? "it is" : "they are"} not counted here.`
      : "";

  if (outstanding === 0) {
    return `Every requirement we can check is met on the answers you have given.${judgedClause}`;
  }
  return `${readiness}% of the requirements we can check are met on your answers. ${outstanding} ${outstanding === 1 ? "is" : "are"} still open.${judgedClause}`;
}
